import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import DirectionsIcon from "@mui/icons-material/Directions";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"; 
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import PlaceIcon from "@mui/icons-material/Place";
import { COLORS, getGoogleMapsUrl } from "./constants";

const StationCard = ({ station, isSelected, onClick, showDistance = true, allStations, index }) => {
  const navigate = useNavigate();

  const displayImage = station.thumbnail || station.mainImage; 

  const handleViewDetails = (e) => {
    e.stopPropagation();
    navigate("/station_details", { 
      state: {
        currentStation: station,
        station: station,
        allStations: allStations || [],
        currentFilter: "All",
      },
    });
  };

  const handleOpenMaps = (e) => {
    e.stopPropagation();
    window.open(getGoogleMapsUrl(station), "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      onClick={onClick}
      elevation={isSelected ? 6 : 0} 
      sx={{
        display: "flex",
        flexDirection: "row",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        border: isSelected 
          ? `2px solid ${COLORS.primary}` 
          : `1px solid transparent`,
        backgroundColor: isSelected ? "#fff" : "transparent",
        borderRadius: "16px",
        mb: 1.5,
        overflow: "hidden", 
        "&:hover": {
          backgroundColor: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* --- LEFT: Image Section --- */}
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 85,
            height: 85,
            borderRadius: "12px",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "#f0f2f5",
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)"
          }}
        >
          {displayImage ? (
            <Box
              component="img"
              src={displayImage}
              alt={station.location}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
               <LocalGasStationIcon sx={{ color: COLORS.primaryLight, fontSize: 32 }} />
            </Box>
          )}
          
          {/* Index Badge */}
          {index && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bgcolor: isSelected ? COLORS.primary : "#455a64",
                color: "#fff",
                minWidth: 24,
                height: 24,
                px: 0.5,
                borderRadius: "0 0 8px 0",
                fontSize: "0.75rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "2px 2px 5px rgba(0,0,0,0.2)"
              }}
            >
              {index}
            </Box>
          )}
        </Box>
      </Box>

      {/* --- RIGHT: Content Section --- */}
      <CardContent 
        sx={{ 
            flex: 1, 
            p: "12px 12px 12px 0 !important", 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0 // Crucial for text truncation in flexbox
        }}
      >
        {/* Header Row */}
        <Box sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ color: COLORS.primary, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {station.province || "Station"}
            </Typography>
            
            {/* UPDATED: Allows 2 lines of text */}
            <Typography 
                variant="h6" 
                title={station.location}
                sx={{ 
                    fontSize: '1rem', 
                    fontWeight: 700, 
                    lineHeight: 1.3, 
                    color: '#2d3436',
                    mt: 0.2,
                    // Line Clamping Logic
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2, // Limits to 2 lines
                }}
            >
                {station.location}
            </Typography>
        </Box>

        {/* Middle: Distance Badge */}
        {showDistance && station.distance !== undefined && (
            <Box sx={{ mt: 'auto', mb: 1 }}>
                <Box sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    bgcolor: COLORS.primaryLight, 
                    px: 1, py: 0.5, 
                    borderRadius: '20px',
                    border: `1px solid ${COLORS.primaryLight}`
                }}>
                    <PlaceIcon sx={{ fontSize: 14, color: COLORS.primary }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: COLORS.primaryDark }}>
                        {station.distance.toFixed(1)} km away
                    </Typography>
                </Box>
            </Box>
        )}

        {/* Footer: Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <Box 
                onClick={handleOpenMaps}
                sx={{ 
                    display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    color: COLORS.textSecondary,
                    fontSize: '0.75rem', fontWeight: 600,
                    "&:hover": { color: COLORS.accent }
                }}
            >
                <DirectionsIcon sx={{ fontSize: 16 }} />
                Directions
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 12, my: 'auto' }} />

            <Box 
                onClick={handleViewDetails}
                sx={{ 
                    display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    color: COLORS.primary,
                    fontSize: '0.75rem', fontWeight: 700,
                    "&:hover": { color: COLORS.primaryDark }
                }}
            >
                Details
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StationCard;